import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { useEffect, useState } from "react";
import { ThemeProvider, useTheme } from "@/components/ThemeProvider";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import AdminLayout from "@/components/AdminLayout";
import TenantBrandProvider from "@/components/TenantBrandProvider";
import Dashboard from "@/pages/admin/Dashboard";
import EventsList from "@/pages/admin/EventsList";
import EventWizard from "@/pages/admin/EventWizard";
import EventDetail from "@/pages/admin/EventDetail";
import EventBasicsManager from "@/pages/admin/EventBasicsManager";
import Attendees from "@/pages/admin/Attendees";
import Tickets from "@/pages/admin/Tickets";
import Communications from "@/pages/admin/Communications";
import FormBuilder from "@/pages/admin/FormBuilder";
import PageBuilder from "@/pages/admin/PageBuilder";
import ScheduleManager from "@/pages/admin/ScheduleManager";
import WalletPass from "@/pages/admin/WalletPass";
import BadgeDesigner from "@/pages/admin/BadgeDesigner";
import LiveCheckin from "@/pages/admin/LiveCheckin";
import SeatArrangement from "@/pages/admin/SeatArrangement";
import Reports from "@/pages/admin/Reports";
import WorkspaceRegistrations from "@/pages/admin/WorkspaceRegistrations";
import Finance from "@/pages/admin/Finance";
import Audit from "@/pages/admin/Audit";
import Settings from "@/pages/admin/Settings";
import SystemNotificationTemplates from "@/pages/admin/SystemNotificationTemplates";
import Profile from "@/pages/admin/Profile";
import GetHelp from "@/pages/admin/GetHelp";
import AiSearch from "@/pages/admin/AiSearch";
import Billing from "@/pages/admin/Billing";
import Onboarding from "@/pages/admin/Onboarding";
import { getAuthHeaders } from "@/lib/auth-fetch";
import ApiKeys from "@/pages/admin/ApiKeys";
import NotificationsInbox from "@/pages/admin/NotificationsInbox";
import MediaLibrary from "@/pages/admin/MediaLibrary";
import EventLanding from "@/pages/public/EventLanding";
import Register from "@/pages/public/Register";
import StatusPage from "@/pages/public/StatusPage";
import SecurityPage from "@/pages/public/SecurityPage";
import TrustPage from "@/pages/public/TrustPage";
import Compare from "@/pages/public/Compare";
import PrivacyPage from "@/pages/public/PrivacyPage";
import TermsPage from "@/pages/public/TermsPage";
import ContactPage from "@/pages/public/ContactPage";
import ResetPasswordPage from "@/pages/public/ResetPasswordPage";
import AcceptTeamInvitePage from "@/pages/public/AcceptTeamInvitePage";
import AcceptOwnershipPage from "@/pages/public/AcceptOwnershipPage";
import Onsite from "@/pages/onsite/Onsite";
import { API_BASE } from "./lib/utils";
import { getAuthSession } from "@/lib/auth-session";
import { isPlatformSession } from "@/lib/platform-api";
import PlatformLayout from "@/components/PlatformLayout";
import PlatformOverview from "@/pages/platform/PlatformOverview";
import PlatformAccounts from "@/pages/platform/PlatformAccounts";
import PlatformAccountDetail from "@/pages/platform/PlatformAccountDetail";
import PlatformLeads from "@/pages/platform/PlatformLeads";
import PlatformSettings from "@/pages/platform/PlatformSettings";
import PlatformBilling from "@/pages/platform/PlatformBilling";
import PlatformBillingSettings from "@/pages/platform/PlatformBillingSettings";
import PlatformLanding from "@/pages/platform/PlatformLanding";
import PlatformDemoEvent from "@/pages/platform/PlatformDemoEvent";
import PlatformStatus from "@/pages/platform/PlatformStatus";
import PlatformTrialInvites from "@/pages/platform/PlatformTrialInvites";
import PlatformAudit from "@/pages/platform/PlatformAudit";
import OperatorCanonicalRedirect from "@/components/OperatorCanonicalRedirect";
import { isCanonicalHost, isOperatorPath, redirectToCanonicalApp } from "@/lib/canonical-host";

function hasAuthSession() {
  const session = getAuthSession();
  return Boolean(session?.email && session?.token);
}

function RequireAuth({ children }) {
  if (hasAuthSession()) return children;
  if (typeof window !== "undefined" && !isCanonicalHost()) {
    return <OperatorCanonicalRedirect />;
  }
  return <Navigate to="/login" replace />;
}

function RequirePlatformAuth({ children }) {
  const location = useLocation();
  if (!hasAuthSession()) {
    if (typeof window !== "undefined" && !isCanonicalHost()) {
      return <OperatorCanonicalRedirect />;
    }
    const params = new URLSearchParams();
    const returnPath = `${location.pathname || ""}${location.search || ""}`;
    if (returnPath.startsWith("/") && !returnPath.startsWith("/login")) {
      params.set("return", returnPath);
    }
    const q = params.toString();
    return <Navigate to={q ? `/login?${q}` : "/login"} replace />;
  }
  if (!isPlatformSession()) return <Navigate to="/admin" replace />;
  return children;
}

function postLoginPath() {
  return isPlatformSession() ? "/platform" : "/admin";
}

function RequireOnboarding({ children }) {
  const location = useLocation();
  const [gate, setGate] = useState("loading");

  useEffect(() => {
    if (location.pathname === "/admin/onboarding") {
      setGate("ok");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/admin/workspace/onboarding`, {
          headers: getAuthHeaders(),
        });
        if (!res.ok) {
          if (!cancelled) setGate("ok");
          return;
        }
        const data = await res.json();
        if (!cancelled) {
          if (data?.required && !data?.completed) setGate("redirect");
          else setGate("ok");
        }
      } catch {
        if (!cancelled) setGate("ok");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  if (gate === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg-base)] text-sm text-[var(--text-secondary)]">
        Loading workspace…
      </div>
    );
  }
  if (gate === "redirect") {
    return <Navigate to="/admin/onboarding" replace />;
  }
  return children;
}

function ThemedToaster() {
  const { theme } = useTheme();
  return (
    <Toaster
      theme={theme}
      position="top-right"
      toastOptions={{
        style: {
          background: "var(--bg-surface)",
          border: "1px solid var(--border-default)",
          color: "var(--text-primary)",
          fontFamily: "DM Sans, sans-serif",
          borderRadius: 0,
        },
      }}
    />
  );
}

// Resolve which event (if any) the visitor's current hostname maps to.
// White-label mode: the operator pointed an external domain at the server
// (e.g. events.example.com -> 203.0.113.10) and pasted that
// hostname into the event's customDomain field. We match the host against
// the bootstrap event list -- if there's a hit, we hijack the routing tree
// so `/` shows that event's landing without any /e/<slug> prefix in the
// URL bar. The match strips the optional `www.` prefix on both sides so
// DNS that lacks an apex record still works.
function findCustomDomainEvent(events) {
  if (typeof window === "undefined") return null;
  const host = String(window.location?.hostname || "").toLowerCase().replace(/^www\./, "");
  if (!host) return null;
  if (!Array.isArray(events)) return null;
  return (
    events.find((ev) => {
      const cd = String(ev?.customDomain || "").toLowerCase().replace(/^www\./, "");
      return cd && cd === host;
    }) || null
  );
}

function App() {
  if (typeof window !== "undefined" && !isCanonicalHost() && isOperatorPath(window.location.pathname)) {
    redirectToCanonicalApp(window.location.pathname, window.location.search);
    return <OperatorCanonicalRedirect />;
  }
  return <AppRoutes />;
}

function AppRoutes() {
  // bootstrapEvents is the source of truth for the white-label hostname
  // lookup. Seed from window.__AIVORY_BOOTSTRAP__.events in case a previous
  // page on this session already populated it (HMR / SPA navigation).
  const [bootstrapEvents, setBootstrapEvents] = useState(() => {
    if (typeof window === "undefined") return null;
    const cached = window.__AIVORY_BOOTSTRAP__?.events;
    return Array.isArray(cached) ? cached : null;
  });
  // bootstrapState tracks the fetch lifecycle independently of whether we
  // got events back. "loaded" means the request finished (success or
  // explicit failure); "timed_out" means we hit the safety fallback.
  const [bootstrapState, setBootstrapState] = useState(
    Array.isArray(bootstrapEvents) ? "loaded" : "loading"
  );

  useEffect(() => {
    let mounted = true;
    // Bumped from 4s to 20s. The old short fallback was tuned for a fast
    // canonical-host load, but on a custom domain the hostname-match needs
    // bootstrap to be present before the first render -- if it isn't, the
    // visitor briefly sees the marketing Landing and there's no re-render
    // unless we track events in state (which we now do). Keep the fallback
    // generous so even mobile 3G has time before we give up.
    const fallback = window.setTimeout(() => {
      if (mounted && bootstrapState === "loading") setBootstrapState("timed_out");
    }, 20000);
    (async () => {
      try {
        // cache: 'no-store' so Safari (and any other aggressive HTTP cache)
        // doesn't serve a stale events list. A freshly-provisioned custom
        // domain has to be visible to the white-label hostname matcher on
        // the very next visit, not whenever the browser feels like
        // revalidating. Same hint we use in EventLanding/Register for
        // consistency across the public surface.
        const res = await fetch(`${API_BASE}/api/bootstrap-data`, { cache: "no-store" });
        if (!res.ok) throw new Error(`Bootstrap failed (${res.status})`);
        const payload = await res.json();
        const data = payload?.data || {};
        window.__AIVORY_BOOTSTRAP__ = data;
        if (mounted) {
          setBootstrapEvents(Array.isArray(data.events) ? data.events : []);
          setBootstrapState("loaded");
        }
      } catch (err) {
        console.warn("Bootstrap data unavailable, using in-file defaults", err);
        if (mounted) setBootstrapState("failed");
      } finally {
        window.clearTimeout(fallback);
      }
    })();
    return () => {
      mounted = false;
      window.clearTimeout(fallback);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const customEvent = findCustomDomainEvent(bootstrapEvents);
  const onCanonical = isCanonicalHost();

  // While bootstrap is still in flight, only render the marketing Landing
  // immediately when we *know* we're on the canonical host. For any other
  // hostname (i.e. a possible white-label domain), keep the loading screen
  // up so the visitor doesn't see Landing flash before EventLanding.
  if (bootstrapState === "loading" && !onCanonical) {
    return <div className="min-h-screen grid place-items-center" style={{ color: "var(--text-secondary)" }}>Loading event...</div>;
  }

  if (customEvent) {
    // White-label mode: public event site only. CMS paths redirect to canonical host.
    return (
      <BrowserRouter>
        <ThemedToaster />
        <Routes>
          <Route path="/signup" element={<OperatorCanonicalRedirect />} />
          <Route path="/admin/*" element={<OperatorCanonicalRedirect />} />
          <Route path="/platform/*" element={<OperatorCanonicalRedirect />} />
          <Route path="/onsite/*" element={<OperatorCanonicalRedirect />} />
          <Route path="/" element={<EventLanding slugOverride={customEvent.slug} />} />
          <Route path="/register" element={<Register slugOverride={customEvent.slug} />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    );
  }

  return (
    <BrowserRouter>
      <ThemedToaster />
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route
          path="/login"
          element={
            hasAuthSession() ? (
              <Navigate to={postLoginPath()} replace />
            ) : (
              <TenantBrandProvider scope="public" applyDocumentEffects={false} className="min-h-screen w-full">
                <Login />
              </TenantBrandProvider>
            )
          }
        />
        <Route
          path="/signup"
          element={
            hasAuthSession() ? (
              <Navigate to="/admin" replace />
            ) : (
              <TenantBrandProvider scope="public">
                <Signup />
              </TenantBrandProvider>
            )
          }
        />

        <Route
          element={(
            <RequirePlatformAuth>
              <PlatformLayout />
            </RequirePlatformAuth>
          )}
        >
          <Route path="/platform" element={<PlatformOverview />} />
          <Route path="/platform/accounts" element={<PlatformAccounts />} />
          <Route path="/platform/accounts/:orgId" element={<PlatformAccountDetail />} />
          <Route path="/platform/billing" element={<PlatformBilling />} />
          <Route path="/platform/billing/settings" element={<PlatformBillingSettings />} />
          <Route path="/platform/landing" element={<PlatformLanding />} />
          <Route path="/platform/demo-event" element={<PlatformDemoEvent />} />
          <Route path="/platform/status" element={<PlatformStatus />} />
          <Route path="/platform/leads" element={<PlatformLeads />} />
          <Route path="/platform/trial-invites" element={<PlatformTrialInvites />} />
          <Route path="/platform/audit" element={<PlatformAudit />} />
          <Route path="/platform/settings" element={<PlatformSettings />} />
        </Route>

        <Route
          path="/admin/onboarding"
          element={(
            <RequireAuth>
              <TenantBrandProvider scope="admin">
                <Onboarding />
              </TenantBrandProvider>
            </RequireAuth>
          )}
        />

        <Route
          element={(
            <RequireAuth>
              <RequireOnboarding>
                <TenantBrandProvider scope="admin">
                  <AdminLayout />
                </TenantBrandProvider>
              </RequireOnboarding>
            </RequireAuth>
          )}
        >
          <Route path="/admin" element={<Dashboard />} />
          <Route path="/admin/events" element={<EventsList />} />
          <Route path="/admin/media" element={<MediaLibrary />} />
          <Route path="/admin/events/new" element={<EventWizard />} />
          <Route path="/admin/events/:id" element={<EventDetail />} />
          <Route path="/admin/events/:id/basics" element={<EventBasicsManager />} />
          <Route path="/admin/events/:id/attendees" element={<Attendees />} />
          <Route path="/admin/events/:id/tickets" element={<Tickets />} />
          <Route path="/admin/events/:id/communications" element={<Communications />} />
          <Route path="/admin/events/:id/landing" element={<PageBuilder />} />
          <Route path="/admin/events/:id/sessions" element={<ScheduleManager />} />
          <Route path="/admin/events/:id/form" element={<FormBuilder />} />
          <Route path="/admin/events/:id/badge" element={<BadgeDesigner />} />
          <Route path="/admin/events/:id/wallet" element={<WalletPass />} />
          <Route path="/admin/events/:id/checkin" element={<LiveCheckin />} />
          <Route path="/admin/events/:id/seats" element={<SeatArrangement />} />
          <Route path="/admin/events/:id/reports" element={<Reports />} />
          <Route path="/admin/finance" element={<Finance />} />
          <Route path="/admin/reports/registrations" element={<WorkspaceRegistrations />} />
          <Route path="/admin/audit" element={<Audit />} />
          <Route path="/admin/settings" element={<Settings />} />
          <Route path="/admin/settings/system-emails" element={<SystemNotificationTemplates />} />
          <Route path="/admin/profile" element={<Profile />} />
          <Route path="/admin/help" element={<GetHelp />} />
          <Route path="/admin/ai" element={<AiSearch />} />
          <Route path="/admin/billing" element={<Billing />} />
          <Route path="/admin/api-keys" element={<ApiKeys />} />
          <Route path="/admin/notifications" element={<NotificationsInbox />} />
        </Route>

        <Route path="/e/:slug" element={<EventLanding />} />
        <Route path="/e/:slug/register" element={<Register />} />
        <Route path="/status" element={<StatusPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/accept-invite" element={<AcceptTeamInvitePage />} />
        <Route path="/accept-ownership" element={<AcceptOwnershipPage />} />
        <Route path="/compare" element={<Compare />} />
        <Route path="/forgot-password" element={<SecurityPage />} />
        <Route path="/security" element={<TrustPage />} />
        <Route path="/trust" element={<TrustPage />} />
        <Route path="/trust-centre" element={<TrustPage />} />
        <Route path="/trust-center" element={<TrustPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/onsite" element={<RequireAuth><Onsite /></RequireAuth>} />
        <Route path="/onsite/:mode" element={<RequireAuth><Onsite /></RequireAuth>} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

function AppWithProviders() {
  return (
    <ThemeProvider>
      <App />
    </ThemeProvider>
  );
}

export default AppWithProviders;
